import { inject, Injectable } from '@angular/core';
import { USER } from '@marmon/frontend/common';
import { Firestore } from '@marmon/frontend/firebase/firestore';
import {
	applyEntitySettingChange,
	EntitySetting,
	EntitySettingsCollection,
	EntitySettingType,
	entitySettingDocId,
} from '@marmon/shared/types/user';
import { sanitize } from '@marmon/shared/utils';
import { doc, getDoc, runTransaction, Timestamp } from 'firebase/firestore';

/**
 * Reads and writes the signed-in user's per-entity settings.
 * Alert delivery is recorded by the API. This service changes preferences.
 */
@Injectable({ providedIn: 'root' })
export class EntitySettingService {
	private readonly firestore = inject(Firestore);
	private readonly user$ = inject(USER);

	ref(entityType: EntitySettingType, entityId: string) {
		return doc(
			this.firestore,
			'users',
			this.requireUserId(),
			EntitySettingsCollection,
			entitySettingDocId(entityType, entityId),
		);
	}

	async get(
		entityType: EntitySettingType,
		entityId: string,
	): Promise<EntitySetting | null> {
		const snapshot = await getDoc(this.ref(entityType, entityId));
		if (!snapshot.exists()) return null;
		return { ...(snapshot.data() as EntitySetting), id: snapshot.id };
	}

	async setSystemMuted(
		entityType: EntitySettingType,
		entityId: string,
		muteSystem: boolean,
	): Promise<EntitySetting> {
		const ref = this.ref(entityType, entityId);
		return runTransaction(this.firestore, async (transaction) => {
			const snapshot = await transaction.get(ref);
			const current = snapshot.exists()
				? { ...(snapshot.data() as EntitySetting), id: snapshot.id }
				: null;
			const next = applyEntitySettingChange(
				current,
				{ entityType, entityId },
				{ muteSystem },
				Timestamp.now(),
			);
			transaction.set(ref, sanitize(next));
			return next;
		});
	}

	private requireUserId(): string {
		const userId = this.user$.value?.id;
		if (!userId) {
			throw new Error('EntitySettingService: user is not signed in');
		}
		return userId;
	}
}
